<template>
  <div id="award-category-list" class="jdx">
    <!-- Ambient background (decorative only) -->
    <div class="jdx-ambient" aria-hidden="true">
      <span class="jdx-glow jdx-glow--aqua"></span>
      <span class="jdx-glow jdx-glow--navy"></span>
      <span class="jdx-glow jdx-glow--seafoam"></span>
    </div>

    <!-- Hero -->
    <section class="jdx-hero jdx-reveal" style="--d: 0ms">
      <div class="jdx-hero__decor" aria-hidden="true">
        <span class="jdx-orb jdx-orb--1"></span>
        <span class="jdx-orb jdx-orb--2"></span>
        <span class="jdx-ring jdx-ring--1"></span>
        <span class="jdx-ring jdx-ring--2"></span>
        <span class="jdx-spark jdx-spark--1"></span>
        <span class="jdx-spark jdx-spark--2"></span>
        <span class="jdx-spark jdx-spark--3"></span>
        <span class="jdx-spark jdx-spark--4"></span>
        <!-- Spreadsheet with upload arrow -->
        <svg class="jdx-hero__cards" viewBox="0 0 220 140" fill="none">
          <g class="jdx-card jdx-card--1">
            <rect x="24" y="30" width="120" height="104" rx="12" />
            <line x1="24" y1="56" x2="144" y2="56" />
            <line x1="24" y1="82" x2="144" y2="82" />
            <line x1="24" y1="108" x2="144" y2="108" />
            <line x1="64" y1="30" x2="64" y2="134" />
          </g>
          <g class="jdx-card jdx-card--2">
            <circle cx="170" cy="58" r="30" />
            <path d="M170 74 V44 M158 56 L170 44 L182 56" />
          </g>
        </svg>
      </div>

      <div class="jdx-hero__top">
        <div class="jdx-hero__grid">
          <span class="jdx-hero__icon">
            <v-icon color="white" size="34">mdi-cloud-upload-outline</v-icon>
          </span>
          <div class="jdx-hero__copy">
            <div class="jdx-hero__eyebrow">
              JCI Awards <span class="jdx-dot" aria-hidden="true"></span> Judges
              <em>Bulk Upload</em>
            </div>
            <h1 class="jdx-hero__title">{{ PageTitle || "Upload Judges" }}</h1>
            <span class="jdx-hero__accent" aria-hidden="true"></span>
            <p class="jdx-hero__desc" v-if="PageDescription">
              {{ PageDescription }}
            </p>
          </div>
        </div>

        <div class="jdx-hero__actions">
          <v-btn
            to="/jci-award/judges/list"
            depressed
            class="jdx-glass-btn"
            aria-label="List"
          >
            <v-icon left class="jdx-glass-btn__icon"
              >mdi-format-list-bulleted</v-icon
            >
            <span class="jdx-glass-btn__text">List</span>
          </v-btn>
          <v-btn
            @click.prevent="refreshPageData"
            depressed
            class="jdx-glass-btn"
            aria-label="Refresh"
          >
            <v-icon left class="jdx-glass-btn__icon jdx-spin-hover"
              >mdi-refresh</v-icon
            >
            <span class="jdx-glass-btn__text">Refresh</span>
          </v-btn>
        </div>
      </div>
    </section>

    <div class="jdx-layout">
      <!-- Upload form -->
      <section class="jdx-studio jdx-reveal" style="--d: 120ms">
        <header class="jdx-studio__head">
          <div class="jdx-head">
            <span class="jdx-head__icon jdx-head__icon--navy">
              <v-icon color="white" size="22">mdi-file-upload-outline</v-icon>
            </span>
            <div>
              <h2 class="jdx-head__title">Upload Details</h2>
              <p class="jdx-head__desc">
                Pick the organization and judge type, then attach the Excel file
              </p>
            </div>
          </div>
          <span class="jdx-required-note">
            <span class="jdx-req">*</span> indicates required field
          </span>
        </header>

        <div class="jdx-inline-loader" v-if="LoadingFlag">
          <v-progress-linear
            indeterminate
            rounded
            height="4"
            color="#0097d7"
          ></v-progress-linear>
        </div>

        <v-form ref="form1" v-model="valid1" lazy-validation id="createForm">
          <div class="jdx-fields">
            <div class="jdx-field">
              <label class="jdx-field__label">
                <span class="jdx-field__icon jdx-field__icon--navy">
                  <v-icon size="16" color="white"
                    >mdi-office-building</v-icon
                  >
                </span>
                <span><span class="jdx-req">*</span> Organization Type</span>
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :items="OrganizationTypeOptions"
                :rules="OrganizationTypeRules"
                v-model="OrganizationType"
                placeholder="Select organization type"
                dense
                clearable
                required
                outlined
              ></v-autocomplete>
            </div>
            <div class="jdx-field">
              <label class="jdx-field__label">
                <span class="jdx-field__icon jdx-field__icon--aqua">
                  <v-icon size="16" color="white">mdi-gavel</v-icon>
                </span>
                <span><span class="jdx-req">*</span> Judge Type</span>
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :items="JudgeTypeOptions"
                :rules="JudgeTypeRules"
                v-model="JudgeType"
                placeholder="Select judge type"
                dense
                clearable
                required
                outlined
              ></v-autocomplete>
            </div>
            <!-- <div class="jdx-field">
              <label class="jdx-field__label">
                <span><span class="jdx-req">*</span> Zone</span>
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :items="ZoneCodeOptions"
                :rules="ZoneCodeRules"
                v-model="ZoneCode"
                dense
                clearable
                required
                outlined
              ></v-autocomplete>
            </div> -->
          </div>

          <!-- File drop area -->
          <div class="jdx-field jdx-drop">
            <label class="jdx-field__label">
              <span class="jdx-field__icon jdx-field__icon--seafoam">
                <v-icon size="16" color="white">mdi-file-excel</v-icon>
              </span>
              <span><span class="jdx-req">*</span> Upload Excel File</span>
            </label>
            <div class="jdx-drop__zone">
              <span class="jdx-drop__icon" aria-hidden="true">
                <v-icon size="26" color="#3a67b1"
                  >mdi-file-excel</v-icon
                >
              </span>
              <v-file-input
                :clearable="false"
                truncate-length="25"
                placeholder="Choose the filled-in Excel file"
                v-model="UploadedFile"
                :rules="UploadedFileRules"
                prepend-icon=""
                outlined
                dense
                class="jdx-drop__input"
              >
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="jdx-row-action jdx-row-action--delete"
                        @click="resetUploadFile"
                      >
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Remove file</span>
                  </v-tooltip>
                </template>
              </v-file-input>
            </div>
          </div>

          <div class="jdx-actions jdx-actions--end">
            <v-btn
              @click.prevent="resetAll"
              depressed
              class="jdx-btn jdx-btn--ghost"
            >
              <v-icon left class="jdx-btn__icon"
                >mdi-close-circle-outline</v-icon
              >
              Clear
            </v-btn>
            <v-btn
              :disabled="!valid1"
              @click.prevent="submitForm"
              :loading="SubmitFlag"
              depressed
              class="jdx-btn jdx-btn--submit"
            >
              <v-icon left class="jdx-btn__icon">mdi-cloud-upload-outline</v-icon>
              Submit Upload
            </v-btn>
          </div>
        </v-form>
      </section>

      <!-- Guide -->
      <aside class="jdx-guide jdx-reveal" style="--d: 200ms">
        <div class="jdx-guide__head">
          <span class="jdx-guide__icon">
            <v-icon color="#0a0f29" size="22">mdi-lightbulb-on-outline</v-icon>
          </span>
          <div>
            <div class="jdx-guide__eyebrow">
              <span class="jdx-req">*</span> Kindly Note
            </div>
            <h3 class="jdx-guide__title">Before you upload</h3>
          </div>
        </div>

        <ol class="jdx-guide__steps">
          <li>
            <span class="jdx-guide__no">1</span>
            <span>
              Please download sample file here and keep its column headings
              unchanged.
            </span>
          </li>
          <li>
            <span class="jdx-guide__no">2</span>
            <span>Fill in the judge details in the sample file columns.</span>
          </li>
          <li>
            <span class="jdx-guide__no">3</span>
            <span>
              Select the organization and judge type, attach the file and
              submit.
            </span>
          </li>
        </ol>

        <a :href="SampleFilePath" target="_blank" class="jdx-sample">
          <span class="jdx-sample__icon">
            <v-icon size="20" color="white">mdi-download</v-icon>
          </span>
          <span class="jdx-sample__text">
            <span class="jdx-sample__title">Download sample file</span>
            <span class="jdx-sample__desc">Excel template (.xlsx)</span>
          </span>
          <v-icon size="18" class="jdx-sample__arrow">mdi-arrow-right</v-icon>
        </a>
      </aside>
    </div>

    <!-- Upload results -->
    <transition name="jdx-fade">
      <section v-if="rows.length > 0" class="jdx-group jdx-reveal">
        <header class="jdx-group__head">
          <span class="jdx-group__icon">
            <v-icon color="white" size="20">mdi-clipboard-check-outline</v-icon>
          </span>
          <div class="jdx-group__copy">
            <h2 class="jdx-group__title">
              Upload Results
              <span class="jdx-count">{{ rows.length }}</span>
            </h2>
            <p class="jdx-group__desc">Row-wise status of the uploaded file</p>
          </div>
        </header>

        <div class="jdx-banners" v-if="SuccessMessage || ErrorMessage">
          <div class="jdx-banner jdx-banner--success" v-if="SuccessMessage">
            <v-icon size="20" color="#2f8f76">mdi-check-circle-outline</v-icon>
            <span>{{ SuccessMessage }}</span>
          </div>
          <div class="jdx-banner jdx-banner--error" v-if="ErrorMessage">
            <v-icon size="20" color="#c53030">mdi-alert-circle-outline</v-icon>
            <span>{{ ErrorMessage }}</span>
          </div>
        </div>

        <div class="jdx-table-card">
          <v-data-table
            v-model="selected"
            class="jdx-table custom-data-table"
            item-class="custom-row-style"
            :headers="TableColumns1"
            :items="rows"
            :items-per-page="50"
            :search="search"
            :single-select="false"
            :show-select="false"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50],
            }"
          >
            <template v-slot:[`item.type`]="{ item }">
              <v-chip
                :color="item.type === 'Success' ? 'green' : 'red'"
                dark
                small
              >
                {{ item.type }}
              </v-chip>
            </template>
            <template v-slot:[`item.message`]="{ item }">
              <div>
                <span
                  v-for="(line, idx) in splitSentences(item.message)"
                  :key="idx"
                  class="d-block mb-1"
                >
                  {{ line }}
                </span>
              </div>
            </template>
            <template v-slot:[`item.Message`]="{ item }">
              <div class="jdx-msg">
                <span
                  v-for="(line, idx) in splitSentences(item.Message)"
                  :key="idx"
                  class="jdx-msg__line"
                >
                  {{ line }}
                </span>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  data() {
    return {
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,
      LoadingFlag: false,
      SelectFlag: true,
      DeleteFlag: false,
      ResultFlag: 0,

      SubmitFlag: false,
      valid1: true,

      SuccessMessage: "",
      ErrorMessage: "",

      Message: "",
      MessageColor: "",

      alert: {},

      valid: true,

      RoleId: "",

      SampleFilePath: "",

      UploadedFile: null,
      UploadedFileRules: [(v) => !!v || "Upload Excel File is required"],

      OrganizationTypeRules: [(v) => !!v || "Organization Type is required"],
      OrganizationType: "",
      OrganizationTypeOptions: [],

      JudgeTypeRules: [(v) => !!v || "Judge Type is required"],
      JudgeType: "",
      JudgeTypeOptions: [
        {
          value: 174,
          text: "Zone Head Table",
        },
        {
          value: 175,
          text: "National Head Table",
        },
        {
          value: 196,
          text: "EO",
        },
        {
          value: 173,
          text: "Judge",
        },
      ],

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCode: "",
      ZoneCodeOptions: [],

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      recordData: {},

      dialog: false,
      dialogDelete: false,
      rows: [],
      TableData1: [],
      TableOptions1: [],
      TableColumns1: [],
      TotalMembers: 0,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
    };
  },
  computed: {
    messageTableItems() {
      const items = [];
      if (this.SuccessMessage) {
        items.push({
          type: "Success",
          message: this.SuccessMessage,
        });
      }
      if (this.ErrorMessage) {
        items.push({
          type: "Error",
          message: this.ErrorMessage,
        });
      }
      return items;
    },
  },

  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        var server_url = companyConfig.apiURL;
        console.log("server_url = " + server_url);
        var SampleFilePath =
          server_url + "assets/sample/head_table_upload_sample.xlsx";
        console.log("SampleFilePath = " + SampleFilePath);
        this.SampleFilePath = SampleFilePath;

        this.getOrganizationTypeOptions();
        // this.getZoneCodeOptions();
      }
    },
    OrganizationTypeOptions: function () {
      console.log("watch OrganizationTypeOptions");
      this.OrganizationTypeOptionsLoading = false;
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "award_judges",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    resetUploadFile() {
      console.log("resetUploadFile called");
      this.UploadedFile = {};
      this.ErrorMessage = "";
      this.ErrorMessageTxt = {};
      this.SuccessMessage = "";
      this.MessageColor = "";
      this.Message = "";
    },
    resetAll() {
      console.log("resetAll called");
      this.UploadedFile = {};
      this.ErrorMessage = "";
      this.ErrorMessageTxt = {};
      this.SuccessMessage = "";
      this.MessageColor = "";
      this.Message = "";
      this.OrganizationType = "";
      this.JudgeType = "";
    },

    getOrganizationTypeOptions() {
      console.log("getOrganizationTypeOptions called");
      var selectbox1_source = "OrganizationType";
      var selectbox1_destination = "OrganizationTypeOptions";
      var selectbox1_url = "api/organization-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var selectbox1_source = "ZoneCode";
      var selectbox1_destination = "ZoneCodeOptions";
      var selectbox1_url = "api/year-wise-lom/zone-options";
      var selectbox1_conditions_array = {
        UserInterface: 4,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    splitSentences(msg) {
      if (!msg) return [];
      return msg.split(/(?<=[.?!])\s+/).filter(Boolean);
    },
    submitForm() {
      console.log("submitForm called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        this.TableOptions1 = {};
        this.TableColumns1 = [];
        this.rows = [];
        this.SubmitFlag = true;

        let upload = new FormData();
        upload.append("UserInterface", 1);
        upload.append("OrganizationType", this.OrganizationType);
        upload.append("Role", this.JudgeType);
        upload.append("UploadFile", this.UploadedFile);

        this.progessStart = 1;
        this.SubmitFlag = true;
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judge/bulk-upload";
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var error = "";
        var success = "";
        var error_message = {};
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.LoadingFlag = false;
            thisIns.progessStart = 0;

            success = response.data.success;
            error = response.data.error;
            flag = response.data.flag;
            records = response.data.records;
            console.log({ flag });
            console.log({ success });
            console.log({ error });
            console.log({ records });
            thisIns.TableColumns1 = records.TableHeader;
            thisIns.rows = records.TableData;
            thisIns.SuccessMessage = success;
            thisIns.ErrorMessage = error;

            // if (flag) {
            //   thisIns.UploadedFile = {};
            //   thisIns.OrganizationType = "";
            //   thisIns.JudgeType = "";
            // }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.progessStart = 0;
            thisIns.SubmitFlag = false;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        // this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.jdx {
  /* JCI approved palette */
  --navy: #3a67b1;
  --aqua: #0097d7;
  --gold: #edbe38;
  --seafoam: #56bda3;
  --ink: #0a0f29;
  --text: #414042;
  --white: #ffffff;
  --danger: #c53030;

  --surface: #ffffff;
  --surface-tint: #f5f8fd;
  --line: #e3e9f3;
  --line-strong: #cdd8ea;
  --muted: #6b7280;

  --radius-lg: 22px;
  --radius-md: 16px;
  --radius-sm: 12px;

  --ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --t-fast: 180ms;
  --t-med: 280ms;

  --shadow-sm: 0 1px 2px rgba(10, 15, 41, 0.04),
    0 4px 14px rgba(58, 103, 177, 0.07);
  --shadow-md: 0 2px 4px rgba(10, 15, 41, 0.04),
    0 16px 36px rgba(58, 103, 177, 0.13);

  position: relative;
  isolation: isolate;
  padding: 4px 0 32px;
  color: var(--text);
  font-family: "Poppins", "Roboto", sans-serif;
  overflow-x: hidden;
}

/* ---------- Ambient background ---------- */
.jdx-ambient {
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
}
.jdx-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
  animation: jdx-drift 26s ease-in-out infinite alternate;
}
.jdx-glow--aqua {
  width: 440px;
  height: 440px;
  top: 300px;
  left: -160px;
  background: rgba(0, 151, 215, 0.3);
}
.jdx-glow--navy {
  width: 400px;
  height: 400px;
  top: 720px;
  right: -140px;
  background: rgba(58, 103, 177, 0.25);
  animation-delay: -9s;
}
.jdx-glow--seafoam {
  width: 320px;
  height: 320px;
  bottom: 80px;
  left: 35%;
  background: rgba(86, 189, 163, 0.2);
  animation-delay: -16s;
}

/* ---------- Entrance reveal ---------- */
.jdx-reveal {
  animation: jdx-rise 640ms var(--ease-out) backwards;
  animation-delay: var(--d, 0ms);
}

/* ---------- Hero ---------- */
.jdx-hero {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  padding: 32px 36px 26px;
  margin-bottom: 22px;
  color: var(--white);
  background: radial-gradient(
      110% 140% at 100% 0%,
      rgba(255, 255, 255, 0.22) 0%,
      rgba(255, 255, 255, 0) 50%
    ),
    radial-gradient(
      80% 120% at 0% 100%,
      rgba(86, 189, 163, 0.45) 0%,
      rgba(86, 189, 163, 0) 60%
    ),
    linear-gradient(125deg, #0082bd 0%, var(--aqua) 50%, #1aaee8 100%);
  box-shadow: 0 24px 60px -24px rgba(0, 151, 215, 0.65);
}
.jdx-hero::before {
  /* slow moving light sweep */
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 45%,
    transparent 60%
  );
  background-size: 250% 100%;
  animation: jdx-sweep 9s linear infinite;
  pointer-events: none;
}
.jdx-hero::after {
  /* fine grid texture */
  content: "";
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.07) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.07) 1px, transparent 1px);
  background-size: 34px 34px;
  mask-image: linear-gradient(90deg, transparent 0%, #000 75%);
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 75%);
  pointer-events: none;
}
.jdx-hero__decor {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.jdx-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  animation: jdx-drift 18s ease-in-out infinite alternate;
}
.jdx-orb--1 {
  width: 240px;
  height: 240px;
  right: 24%;
  bottom: -150px;
  background: rgba(86, 189, 163, 0.45);
}
.jdx-orb--2 {
  width: 170px;
  height: 170px;
  left: 42%;
  top: -90px;
  background: rgba(237, 190, 56, 0.26);
  animation-delay: -7s;
}
.jdx-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.jdx-ring--1 {
  width: 320px;
  height: 320px;
  right: -90px;
  top: -150px;
  border-style: dashed;
  animation: jdx-spin 60s linear infinite;
}
.jdx-ring--2 {
  width: 200px;
  height: 200px;
  right: -30px;
  top: -90px;
}
.jdx-spark {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--white);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.9),
    0 0 18px rgba(255, 255, 255, 0.6);
  animation: jdx-twinkle 3.2s ease-in-out infinite;
}
.jdx-spark--1 {
  left: 56%;
  top: 18%;
}
.jdx-spark--2 {
  left: 72%;
  top: 64%;
  animation-delay: -1.1s;
}
.jdx-spark--3 {
  left: 8%;
  top: 88%;
  width: 4px;
  height: 4px;
  animation-delay: -2s;
}
.jdx-spark--4 {
  left: 34%;
  top: 12%;
  width: 4px;
  height: 4px;
  background: var(--gold);
  box-shadow: 0 0 12px rgba(237, 190, 56, 0.9);
  animation-delay: -0.6s;
}
.jdx-hero__cards {
  position: absolute;
  right: 36px;
  bottom: -18px;
  width: 220px;
  height: 140px;

  rect,
  circle {
    fill: rgba(255, 255, 255, 0.12);
    stroke: rgba(255, 255, 255, 0.32);
    stroke-width: 1;
  }
  line {
    stroke: rgba(255, 255, 255, 0.38);
    stroke-width: 2;
    stroke-linecap: round;
  }
  .jdx-card {
    transform-box: fill-box;
    transform-origin: bottom center;
    animation: jdx-deal 900ms var(--ease-out) backwards;
  }
  .jdx-card--1 {
    transform: rotate(-8deg);
    animation-delay: 120ms;
  }
  .jdx-card--2 {
    animation-delay: 220ms;

    rect {
      fill: rgba(237, 190, 56, 0.3);
      stroke: rgba(237, 190, 56, 0.8);
    }
    circle {
      fill: rgba(237, 190, 56, 0.85);
      stroke: none;
    }
  }
  .jdx-card--3 {
    transform: rotate(8deg);
    animation-delay: 320ms;
  }
}

.jdx-hero__top {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px 24px;
}
.jdx-hero__grid {
  display: flex;
  align-items: center;
  gap: 22px;
  min-width: 0;
  flex: 1 1 420px;
}
.jdx-hero__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3),
    rgba(255, 255, 255, 0.08)
  );
  border: 1px solid rgba(255, 255, 255, 0.36);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 14px 30px -12px rgba(10, 15, 41, 0.45);
  backdrop-filter: blur(6px);
  animation: jdx-pop 700ms var(--ease-out) 200ms backwards;
}
.jdx-hero__copy {
  min-width: 0;
}
.jdx-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;

  em {
    font-family: "Lora", Georgia, serif;
    font-style: italic;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: none;
    font-size: 1.02rem;
    color: var(--gold);
    text-shadow: 0 1px 8px rgba(10, 15, 41, 0.25);
  }
}
.jdx-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--gold);
}
.jdx-hero__title {
  margin: 0;
  font-size: clamp(1.5rem, 1.05rem + 1.6vw, 2.3rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: var(--white);
  text-shadow: 0 2px 14px rgba(10, 15, 41, 0.18);
  overflow-wrap: anywhere;
}
.jdx-hero__accent {
  display: block;
  width: 92px;
  height: 3px;
  margin: 12px 0 10px;
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    var(--gold),
    var(--white),
    var(--seafoam),
    var(--gold)
  );
  background-size: 300% 100%;
  transform-origin: left center;
  animation: jdx-gradient 6s linear infinite,
    jdx-grow 900ms var(--ease-out) 300ms both;
}
.jdx-hero__desc {
  margin: 0;
  max-width: 600px;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.92);
}

/* Hero buttons */
.jdx-hero__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.jdx-glass-btn.v-btn,
.jdx-gold-btn.v-btn {
  height: 42px;
  padding: 0 18px;
  border-radius: 13px;
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  transition: background-color var(--t-fast) var(--ease),
    transform var(--t-med) var(--ease), box-shadow var(--t-med) var(--ease);

  &:hover {
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.75);
    outline-offset: 2px;
  }
}
.jdx-glass-btn.v-btn {
  color: var(--white) !important;
  background: rgba(255, 255, 255, 0.14) !important;
  border: 1px solid rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(6px);

  .jdx-glass-btn__icon {
    color: var(--white) !important;
    transition: transform 500ms var(--ease);
  }
  &:hover {
    background: rgba(255, 255, 255, 0.24) !important;
    box-shadow: 0 12px 24px -14px rgba(10, 15, 41, 0.6);
  }
  &:hover .jdx-spin-hover {
    transform: rotate(180deg);
  }
}
.jdx-gold-btn.v-btn {
  color: var(--ink) !important;
  background: var(--gold) !important;
  box-shadow: 0 12px 26px -12px rgba(10, 15, 41, 0.55);

  .jdx-gold-btn__icon {
    color: var(--ink) !important;
    transition: transform var(--t-med) var(--ease);
  }
  &:hover {
    box-shadow: 0 18px 32px -12px rgba(10, 15, 41, 0.6);
    filter: brightness(1.04);
  }
  &:hover .jdx-gold-btn__icon {
    transform: scale(1.12);
  }
}

/* ---------- Shared section head ---------- */
.jdx-head {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.jdx-head__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  transition: transform var(--t-med) var(--ease);
}
.jdx-head__icon--navy {
  background: linear-gradient(135deg, var(--navy), var(--aqua));
  box-shadow: 0 10px 22px -10px rgba(58, 103, 177, 0.8);
}
.jdx-head__title {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.3;
}
.jdx-head__desc {
  margin: 2px 0 0;
  font-size: 0.84rem;
  color: var(--muted);
}

/* ---------- Judge finder ---------- */
.jdx-studio {
  position: relative;
  padding: 24px 28px 22px;
  margin-bottom: 26px;
  border-radius: var(--radius-lg);
  background: linear-gradient(180deg, #ffffff, var(--surface-tint));
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--t-med) var(--ease);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 28px;
    right: 28px;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: linear-gradient(
      90deg,
      var(--navy),
      var(--aqua),
      var(--seafoam),
      var(--gold)
    );
  }
  &:hover {
    box-shadow: var(--shadow-md);
  }
  &:hover .jdx-head__icon {
    transform: rotate(-6deg) scale(1.05);
  }
}
.jdx-studio__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px 20px;
  margin-bottom: 20px;
}

/* Loading */
.jdx-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 26px 0 18px;
}
.jdx-loading__orb {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.jdx-loading__halo {
  position: absolute;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: radial-gradient(rgba(0, 151, 215, 0.25), transparent 70%);
  animation: jdx-breathe 2.4s ease-in-out infinite;
}
.jdx-loading__icon {
  position: absolute !important;
}
.jdx-loading__text {
  margin: 14px 0 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--muted);
}

/* Fields */
.jdx-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 22px;
  max-width: 820px;
}
.jdx-field__label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ink);
}
.jdx-field__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
}
.jdx-field__icon--navy {
  background: var(--navy);
}
.jdx-field__icon--aqua {
  background: var(--aqua);
}
.jdx-req {
  color: var(--danger);
}
.jdx-field,
.jdx-toolbar {
  ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
    background: var(--white);
    border-radius: var(--radius-sm);
    transition: box-shadow var(--t-med) var(--ease);
  }
  ::v-deep .v-text-field--outlined fieldset {
    border-color: var(--line-strong);
    border-radius: var(--radius-sm);
    transition: border-color var(--t-fast) var(--ease);
  }
  ::v-deep
    .v-text-field--outlined:not(.v-input--is-focused):not(.error--text)
    > .v-input__control
    > .v-input__slot:hover
    fieldset {
    border-color: var(--navy);
  }
  ::v-deep .v-input--is-focused > .v-input__control > .v-input__slot {
    box-shadow: 0 0 0 4px rgba(0, 151, 215, 0.14);
  }
  ::v-deep .v-input--is-focused:not(.error--text) fieldset {
    border-color: var(--aqua);
  }
  ::v-deep .v-input__prepend-inner .v-icon {
    color: var(--navy);
  }
}

/* Finder actions */
.jdx-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
  padding-top: 18px;
  border-top: 1px dashed var(--line-strong);
}
.jdx-required-note {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--muted);
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--white);
  border: 1px solid var(--line);
}
.jdx-btn.v-btn {
  height: 46px;
  padding: 0 22px;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
  transition: transform var(--t-med) var(--ease),
    box-shadow var(--t-med) var(--ease), filter var(--t-fast) var(--ease);

  .jdx-btn__icon {
    transition: transform var(--t-med) var(--ease);
  }
  &:not(.v-btn--disabled):hover {
    transform: translateY(-2px);
    filter: brightness(1.05);
  }
  &:not(.v-btn--disabled):hover .jdx-btn__icon {
    transform: translateX(-1px) scale(1.12);
  }
  &:not(.v-btn--disabled):active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 3px solid rgba(0, 151, 215, 0.55);
    outline-offset: 2px;
  }
}
.jdx-btn--search.v-btn:not(.v-btn--disabled) {
  min-width: 190px;
  background: var(--navy) !important;
  color: var(--white) !important;
  box-shadow: 0 12px 26px -12px rgba(58, 103, 177, 0.9);

  .v-icon {
    color: var(--white) !important;
  }
  &:hover {
    box-shadow: 0 18px 32px -12px rgba(58, 103, 177, 0.9);
  }
}
.jdx-btn--export.v-btn {
  height: 42px;
  background: var(--seafoam) !important;
  color: var(--ink) !important;
  box-shadow: 0 12px 26px -14px rgba(86, 189, 163, 0.95);

  .v-icon {
    color: var(--ink) !important;
  }
}

/* ---------- Results group ---------- */
.jdx-group {
  position: relative;
  margin-bottom: 30px;
}
.jdx-group__head {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  padding: 0 0 14px;
  margin-bottom: 16px;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      var(--aqua) 0%,
      rgba(0, 151, 215, 0.12) 35%,
      transparent 100%
    );
  }
}
.jdx-group__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--aqua);
  box-shadow: 0 10px 20px -12px var(--aqua);
}
.jdx-group__copy {
  flex: 1 1 260px;
  min-width: 0;
}
.jdx-group__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--ink);
}
.jdx-group__desc {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: var(--muted);
}
.jdx-count {
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--navy);
  background: rgba(58, 103, 177, 0.1);
  font-variant-numeric: tabular-nums;
}
.jdx-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.jdx-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text);
  background: var(--white);
  border: 1px solid var(--line);
}
.jdx-legend__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.jdx-legend__dot--active {
  background: var(--seafoam);
  box-shadow: 0 0 0 3px rgba(86, 189, 163, 0.2);
}
.jdx-legend__dot--inactive {
  background: var(--danger);
  box-shadow: 0 0 0 3px rgba(197, 48, 48, 0.15);
}

/* Table card */
.jdx-table-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--t-med) var(--ease),
    border-color var(--t-med) var(--ease);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 22px;
    width: 56px;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: var(--aqua);
    z-index: 1;
    transition: width var(--t-med) var(--ease);
  }
  &:hover {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-md);
  }
  &:hover::before {
    width: 110px;
  }
}
.jdx-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(180deg, #ffffff, var(--surface-tint));
}
.jdx-toolbar__search {
  flex: 0 1 380px;
  min-width: 0;
}

.jdx-table {
  ::v-deep thead th {
    height: 48px !important;
    background: var(--surface-tint) !important;
    color: var(--muted) !important;
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 1px solid var(--line) !important;
  }
  ::v-deep tbody tr {
    transition: background-color var(--t-fast) var(--ease);

    &:hover {
      background: rgba(0, 151, 215, 0.04) !important;
    }
    td {
      height: 66px !important;
      font-size: 0.84rem !important;
      color: var(--text);
      border-bottom: 1px solid #eef2f8 !important;
    }
  }
  ::v-deep .v-data-footer {
    padding: 4px 16px;
    border-top: 1px solid var(--line);
    font-size: 0.8rem;
    color: var(--muted);
  }
}

.jdx-row-actions {
  display: flex;
  justify-content: center;
  gap: 6px;
}
.jdx-row-action.v-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--white);
  transition: transform var(--t-fast) var(--ease),
    background-color var(--t-fast) var(--ease),
    border-color var(--t-fast) var(--ease);

  &:hover {
    transform: translateY(-1px);
  }
}
.jdx-row-action--view.v-btn {
  color: var(--aqua) !important;

  &:hover {
    background: rgba(0, 151, 215, 0.08);
    border-color: rgba(0, 151, 215, 0.4);
  }
}
.jdx-row-action--delete.v-btn {
  color: var(--danger) !important;

  &:hover {
    background: rgba(197, 48, 48, 0.07);
    border-color: rgba(197, 48, 48, 0.35);
  }
}

/* ---------- Transitions ---------- */
.jdx-fade-enter-active,
.jdx-fade-leave-active {
  transition: opacity 350ms cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 350ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.jdx-fade-enter,
.jdx-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

/* ---------- Form page extras ---------- */
.jdx-head__icon--aqua {
  background: linear-gradient(135deg, var(--aqua), var(--seafoam));
  box-shadow: 0 10px 22px -10px rgba(0, 151, 215, 0.8);
}
.jdx-head__icon--seafoam {
  background: linear-gradient(135deg, var(--seafoam), var(--aqua));
  box-shadow: 0 10px 22px -10px rgba(86, 189, 163, 0.85);
}
.jdx-field__icon--seafoam {
  background: var(--seafoam);
}
.jdx-optional {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--muted);
  background: var(--surface-tint);
  border: 1px solid var(--line);
}

/* Hero decoration tweaks for form pages */
.jdx-hero__cards {
  path {
    stroke: var(--ink);
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .jdx-card--plus {
    transform-box: fill-box;
    transform-origin: center;
    animation: jdx-pop 700ms var(--ease-out) 450ms backwards;

    circle {
      fill: var(--gold);
      stroke: rgba(255, 255, 255, 0.6);
    }
    line {
      stroke: var(--ink);
      stroke-width: 2.5;
    }
  }
}

/* Hero progress steps */
.jdx-steps {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  max-width: 820px;
  margin: 26px 0 0;
  padding: 0;
  list-style: none;
}
.jdx-step {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 12px 14px;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  transition: background-color var(--t-med) var(--ease),
    border-color var(--t-med) var(--ease);
}
.jdx-step__no {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--white);
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: background-color var(--t-med) var(--ease),
    color var(--t-med) var(--ease);
}
.jdx-step__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.jdx-step__title {
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--white);
}
.jdx-step__desc {
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.jdx-step.is-done {
  .jdx-step__no {
    background: var(--seafoam);
    border-color: rgba(255, 255, 255, 0.45);
  }
}
.jdx-step.is-current {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(237, 190, 56, 0.85);

  .jdx-step__no {
    color: var(--ink);
    background: var(--gold);
    border-color: transparent;
  }
}

/* Form blocks */
.jdx-block {
  position: relative;
  padding: 4px 0 22px;

  & + .jdx-block {
    padding-top: 22px;
    border-top: 1px dashed var(--line-strong);
  }
}
.jdx-block__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
}
.jdx-block__no {
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--aqua);
  text-shadow: 0 6px 14px rgba(0, 151, 215, 0.18);
}
.jdx-block__note {
  margin-left: auto;
}
.jdx-fields--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  max-width: none;
}
.jdx-field--wide {
  grid-column: 1 / -1;
}
.jdx-field {
  ::v-deep .v-chip.v-size--small {
    border-radius: 8px;
    font-weight: 600;
    background: rgba(58, 103, 177, 0.1) !important;
    color: var(--navy) !important;
  }
}

/* Member lookup */
.jdx-lookup {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0 16px;
}
.jdx-lookup__field {
  flex: 0 1 460px;
  min-width: 0;
}
.jdx-btn.v-btn.jdx-lookup__btn {
  height: 40px;
  margin-top: 34px;
}

/* Member profile */
.jdx-block--review {
  padding-bottom: 26px;
}
.jdx-profile {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 18px;
}
.jdx-profile__id {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 26px 18px;
  border-radius: var(--radius-lg);
  color: var(--white);
  background: radial-gradient(
      120% 160% at 100% 0%,
      rgba(0, 151, 215, 0.6),
      transparent 60%
    ),
    linear-gradient(135deg, var(--navy), var(--ink));
  box-shadow: 0 18px 36px -18px rgba(58, 103, 177, 0.9);
}
.jdx-profile__glow {
  position: absolute;
  width: 200px;
  height: 200px;
  top: -80px;
  right: -60px;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  animation: jdx-spin 60s linear infinite;
}
.jdx-profile__avatar {
  position: relative;
  margin-bottom: 14px;
  animation: jdx-pop 700ms var(--ease-out) 120ms backwards;
}
.jdx-profile__img {
  border: 4px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.12),
    0 16px 30px -12px rgba(10, 15, 41, 0.7);
}
.jdx-profile__badge {
  position: absolute;
  right: 4px;
  bottom: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--seafoam);
  border: 3px solid var(--white);
}
.jdx-profile__name {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--white);
  overflow-wrap: anywhere;
}
.jdx-profile__mid {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(237, 190, 56, 0.55);
}
.jdx-profile__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-content: start;
}
.jdx-info {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: var(--radius-md);
  background: var(--surface-tint);
  border: 1px solid var(--line);
  transition: transform var(--t-med) var(--ease),
    border-color var(--t-med) var(--ease), box-shadow var(--t-med) var(--ease);

  &:hover {
    transform: translateY(-2px);
    border-color: var(--line-strong);
    box-shadow: 0 12px 24px -16px rgba(10, 15, 41, 0.45);
  }
}
.jdx-info--wide {
  grid-column: 1 / -1;
}
.jdx-info__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--white);
  border: 1px solid var(--line);
}
.jdx-info__body {
  min-width: 0;
}
.jdx-info__label {
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
.jdx-info__value {
  margin-top: 2px;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.45;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.jdx-chip.v-chip {
  font-weight: 600;
  border-radius: 8px;
}

/* Footer actions */
.jdx-actions--end {
  justify-content: flex-end;
  margin-top: 0;
}
.jdx-btn--ghost.v-btn {
  min-width: 130px;
  color: var(--text) !important;
  background: var(--white) !important;
  border: 1px solid var(--line-strong);

  .v-icon {
    color: var(--danger) !important;
  }
  &:hover {
    border-color: rgba(197, 48, 48, 0.4);
    background: rgba(197, 48, 48, 0.04) !important;
  }
}
.jdx-btn--submit.v-btn:not(.v-btn--disabled) {
  min-width: 190px;
  color: var(--white) !important;
  background: linear-gradient(135deg, var(--navy), var(--aqua)) !important;
  box-shadow: 0 12px 26px -12px rgba(58, 103, 177, 0.9);

  .v-icon {
    color: var(--white) !important;
  }
  &:hover {
    box-shadow: 0 18px 32px -12px rgba(58, 103, 177, 0.9);
  }
}

/* ---------- Upload layout ---------- */
.jdx-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 22px;
  align-items: start;
  margin-bottom: 26px;

  .jdx-studio {
    margin-bottom: 0;
  }
}
.jdx-inline-loader {
  margin: -6px 0 16px;
}
.jdx-drop {
  margin-top: 4px;
}
.jdx-drop__zone {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 16px 0;
  border-radius: var(--radius-md);
  border: 1.5px dashed var(--line-strong);
  background: linear-gradient(
    135deg,
    rgba(58, 103, 177, 0.04),
    rgba(0, 151, 215, 0.04)
  );
  transition: border-color var(--t-med) var(--ease),
    background-color var(--t-med) var(--ease);

  &:hover {
    border-color: var(--aqua);
  }
}
.jdx-drop__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: var(--white);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);
}
.jdx-drop__input {
  flex: 1 1 auto;
  min-width: 0;

  ::v-deep .v-input__append-outer {
    margin-top: 4px !important;
  }
}

/* Guide card */
.jdx-guide {
  position: relative;
  overflow: hidden;
  padding: 22px 22px 20px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--gold), rgba(237, 190, 56, 0.2));
  }
}
.jdx-guide__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.jdx-guide__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: var(--gold);
  box-shadow: 0 10px 20px -10px rgba(237, 190, 56, 0.9);
}
.jdx-guide__eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a6a0f;
}
.jdx-guide__title {
  margin: 2px 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ink);
}
.jdx-guide__steps {
  margin: 0 0 18px;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 12px;
    padding: 10px 0;
    font-size: 0.86rem;
    line-height: 1.5;
    color: var(--text);
    border-bottom: 1px solid var(--line);

    &:last-child {
      border-bottom: 0;
    }
  }
}
.jdx-guide__no {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--navy);
  background: rgba(58, 103, 177, 0.1);
}
.jdx-sample {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: var(--radius-md);
  text-decoration: none !important;
  background: rgba(86, 189, 163, 0.12);
  border: 1px solid rgba(86, 189, 163, 0.5);
  transition: transform var(--t-med) var(--ease),
    box-shadow var(--t-med) var(--ease);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -14px rgba(86, 189, 163, 0.9);
  }
  &:hover .jdx-sample__arrow {
    transform: translateX(3px);
  }
  &:focus-visible {
    outline: 3px solid rgba(0, 151, 215, 0.55);
    outline-offset: 2px;
  }
}
.jdx-sample__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: var(--seafoam);
}
.jdx-sample__text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.jdx-sample__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--ink);
}
.jdx-sample__desc {
  font-size: 0.76rem;
  color: var(--muted);
}
.jdx-sample__arrow {
  color: var(--ink) !important;
  transition: transform var(--t-med) var(--ease);
}

/* Upload result banners */
.jdx-banners {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}
.jdx-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1.5;
  color: var(--text);
}
.jdx-banner--success {
  background: rgba(86, 189, 163, 0.12);
  border: 1px solid rgba(86, 189, 163, 0.5);
}
.jdx-banner--error {
  background: rgba(197, 48, 48, 0.06);
  border: 1px solid rgba(197, 48, 48, 0.3);
}
.jdx-msg {
  padding: 6px 0;
}
.jdx-msg__line {
  display: block;
  line-height: 1.55;

  & + .jdx-msg__line {
    margin-top: 4px;
  }
}

/* ---------- Responsive ---------- */
@media (max-width: 1263px) {
  .jdx-hero__cards {
    width: 180px;
    right: 20px;
    opacity: 0.8;
  }
  .jdx-layout {
    grid-template-columns: minmax(0, 1fr) 300px;
  }
}
@media (max-width: 959px) {
  .jdx-hero {
    padding: 28px 26px 24px;
  }
  .jdx-hero__cards {
    opacity: 0.45;
  }
  .jdx-fields--3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .jdx-profile {
    grid-template-columns: minmax(0, 1fr);
  }
  .jdx-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 599px) {
  .jdx {
    padding-bottom: 20px;
  }
  .jdx-hero {
    padding: 22px 18px 20px;
    border-radius: 22px;
    margin-bottom: 16px;
  }
  .jdx-hero__cards,
  .jdx-ring {
    display: none;
  }
  .jdx-hero__grid {
    align-items: flex-start;
    gap: 14px;
    flex-basis: 100%;
  }
  .jdx-hero__icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
  }
  .jdx-steps {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    margin-top: 20px;
  }
  .jdx-studio {
    padding: 18px 16px;
    margin-bottom: 18px;
  }
  .jdx-fields,
  .jdx-fields--3,
  .jdx-profile__grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .jdx-block__note {
    margin-left: 0;
  }
  .jdx-lookup__field {
    flex-basis: 100%;
  }
  .jdx-btn.v-btn.jdx-lookup__btn {
    margin-top: 0;
    margin-bottom: 8px;
  }
  .jdx-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .jdx-btn.v-btn {
    width: 100%;
  }
  .jdx-drop__icon {
    display: none;
  }
}

/* ---------- Reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
  .jdx *,
  .jdx *::before,
  .jdx *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    animation-delay: 0ms !important;
    transition-duration: 1ms !important;
  }
  .jdx-spark,
  .jdx-hero::before {
    display: none;
  }
}

/* ---------- Keyframes ---------- */
@keyframes jdx-rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes jdx-pop {
  from {
    opacity: 0;
    transform: scale(0.6) rotate(-12deg);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes jdx-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes jdx-deal {
  from {
    opacity: 0;
    transform: translateY(40px) rotate(0deg);
  }
}
@keyframes jdx-gradient {
  from {
    background-position: 0% 50%;
  }
  to {
    background-position: 300% 50%;
  }
}
@keyframes jdx-sweep {
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -100% 0;
  }
}
@keyframes jdx-drift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(30px, -20px) scale(1.06);
  }
  100% {
    transform: translate(-20px, 25px) scale(0.96);
  }
}
@keyframes jdx-twinkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.7);
  }
}
@keyframes jdx-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes jdx-breathe {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}
</style>
